/**
 * #594 — the three quick-size slots in Settings → Preferences.
 *
 * Its own component because it owns a model the rest of the page does not:
 * the STORE holds a compact, deduped list (`[10, 20, 30]`), while the EDITOR
 * is three fixed positions. The previous inline version re-derived one from
 * the other on every keystroke, which normalised half-typed input as if it
 * were final. Four traced consequences, all one bug seen from four angles:
 *
 *   clearing slot 1 of [10,20,30] spliced the list, so the box under the
 *   cursor became 20 and the third emptied;
 *
 *   typing into slot 3 while 1 and 2 were blank wrote a sparse array that
 *   normalised down to index 0, so the value jumped to the first box;
 *
 *   editing slot 1 from 5 to 100 destroyed slot 2's 1 permanently — the
 *   transient [1,1] deduped and the peer never came back. Silent data loss,
 *   not jank;
 *
 *   typing 0 was discarded with no state change, so the keystroke vanished.
 *
 * The fix is to stop deriving positions from a list that has none. The draft
 * below is positional and authoritative; the store is a commit target. A
 * value that cannot be committed (invalid, or a duplicate the store would
 * drop) stays visible in its own box with the hint saying why, and no
 * neighbouring slot is ever moved or cleared by a keystroke.
 */
import { useState } from 'react';

import { POLY_MIN_ORDER_CONTRACTS } from '@/lib/polymarket/closePosition';
import { useDisplaySettings } from '@/stores/displaySettings';

const SLOT_COUNT = 3;

export type QuickSizeHintKind = 'below-one' | 'duplicate' | 'below-venue-minimum';

export interface QuickSizeHint {
  kind: QuickSizeHintKind;
  text: string;
}

/**
 * Parse one slot's text the way a number input means it.
 *
 * `Number`, not `parseInt`. parseInt prefix-parses and stops at the first
 * character it does not understand, so `1e3` — which a type="number" box
 * accepts and a trader means as 1000 — became 1, silently, and the box then
 * disagreed with both the store and the ticket's chips. `Number('1e3')` is
 * 1000. Non-integers are rejected rather than floored for the same reason:
 * `2.5` floored to 2 is a value nobody typed.
 */
function parseSlot(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) return null;
  return n;
}

/**
 * The one line shown under the slots. ONE slot, not one per condition: two
 * competing hint treatments on a control this small read as an error state.
 *
 * Blocking conditions outrank the informational one. A duplicate or a
 * sub-1 value is the reason a keystroke did not reach the store, so the
 * trader needs THAT before being told about a venue floor that merely
 * changes what a valid preset submits.
 *
 * The venue-minimum line is deliberately venue-NEUTRAL in its trigger:
 * `polymarketEnabled` lives in the relay's TraderRules and is not exposed on
 * the trader's own session, so Settings cannot know whether this trader ever
 * touches Polymarket. Worded as a fact about the other venue rather than a
 * complaint, it reads as information to a Kalshi-only trader instead of a nag.
 */
export function quickSizeSlotHint(
  slots: readonly string[],
  venueMinimum: number,
): QuickSizeHint | null {
  const firstSlotOf = new Map<number, number>();
  for (let i = 0; i < slots.length; i += 1) {
    const raw = slots[i].trim();
    if (raw === '') continue;
    const n = parseSlot(raw);
    if (n == null) {
      return {
        kind: 'below-one',
        text: `Slot ${i + 1}: quick sizes must be a whole number of at least 1 — not saved until fixed.`,
      };
    }
    const first = firstSlotOf.get(n);
    if (first != null) {
      return {
        kind: 'duplicate',
        text: `Slot ${i + 1}: ${n} is already set in slot ${first + 1} — not saved until fixed.`,
      };
    }
    firstSlotOf.set(n, i);
  }
  for (const raw of slots) {
    const n = parseSlot(raw);
    if (n != null && n < venueMinimum) {
      return {
        kind: 'below-venue-minimum',
        text:
          `Polymarket's minimum is ${venueMinimum} contracts — ${n} fires as ` +
          `${venueMinimum} on Polymarket tickets.`,
      };
    }
  }
  return null;
}

/** Seed the positional draft from the store's compact list. */
function slotsFromStored(stored: readonly number[]): string[] {
  const next = Array.from({ length: SLOT_COUNT }, () => '');
  stored.slice(0, SLOT_COUNT).forEach((n, i) => {
    next[i] = String(n);
  });
  return next;
}

export function QuickSizeSlots() {
  const setQuickSizes = useDisplaySettings((s) => s.setQuickSizes);
  // Seeded ONCE. Never re-derived from the store: the store is compact and
  // this is positional, so re-deriving would move values between boxes — the
  // very defect above. It also means an edit from another tab does not stomp
  // a draft in progress; the draft wins when it commits.
  const [slots, setSlots] = useState<string[]>(() =>
    slotsFromStored(useDisplaySettings.getState().quickSizes),
  );

  const hint = quickSizeSlotHint(slots, POLY_MIN_ORDER_CONTRACTS);

  const write = (slot: number, value: string) => {
    const next = slots.map((v, i) => (i === slot ? value : v));
    setSlots(next);

    // A blocking state does NOT commit. Committing through one was not merely
    // untidy: the store dedupes first-wins, so a transient duplicate made it
    // DROP the slot that legitimately held the value. Seeding [5, 1] and
    // typing 1 over the 5 persisted [1] at once — slot 2's preset was gone on
    // the next reload, from a keystroke in slot 1. The draft kept showing it,
    // which is why the on-screen promise held while the stored state did not.
    const blocking = quickSizeSlotHint(next, POLY_MIN_ORDER_CONTRACTS);
    if (blocking && blocking.kind !== 'below-venue-minimum') return;

    // Otherwise commit the whole positional draft in order. Empty slots drop
    // out; nothing is spliced, so no keystroke can shift or destroy a
    // neighbour.
    setQuickSizes(next.map(parseSlot).filter((n): n is number => n != null));
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        {slots.map((value, slot) => (
          <input
            // Slot index is the identity here — these boxes are positions,
            // not values, and keying on the value would remount an input
            // mid-edit.
            key={slot}
            type="number"
            min={1}
            step={1}
            value={value}
            placeholder="—"
            aria-label={`Quick size slot ${slot + 1}`}
            onChange={(e) => write(slot, e.target.value)}
            className="border-border bg-input text-foreground placeholder-muted-foreground focus:border-primary w-20 rounded-md border px-2.5 py-1.5 font-mono text-xs focus:outline-none"
          />
        ))}
      </div>
      {hint && (
        <p
          data-testid="quick-size-hint"
          data-hint-kind={hint.kind}
          className="text-muted-foreground mt-1.5 text-xs leading-snug"
        >
          {hint.text}
        </p>
      )}
    </div>
  );
}
